/**
 * Investor Agreement Tab — signature certificate and status
 *
 * Route: /admin/reporting/investors/:id/agreement
 */

import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { useInvestorAgreement } from '@/features/reporting/hooks/useReportingData'
import type { AgreementSignatureItem } from '@/features/reporting/api/reportingApi'

export const Route = createFileRoute({
  component: InvestorAgreementPage,
})

// =============================================================================
// Helpers
// =============================================================================

function formatDateTime(isoStr: string): string {
  return new Date(isoStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

// =============================================================================
// PDF Generation (zero-dependency, uses browser print-to-PDF)
// =============================================================================

function downloadSignaturePdf(sig: AgreementSignatureItem) {
  const fields: [string, string][] = [
    ['Full Legal Name', sig.full_legal_name],
    ['Email', sig.user_email],
    ['IP Address', sig.ip_address],
    ['User Agent', sig.user_agent],
    ['Session ID', sig.session_id],
    ['Agreement Version', `v${sig.agreement_version}`],
    ['Signed At', formatDateTime(sig.signed_at)],
  ]

  if (sig.name_mismatch_confirmed && sig.name_mismatch_confirmed_at) {
    fields.push(['Name Mismatch', `Confirmed at ${formatDateTime(sig.name_mismatch_confirmed_at)}`])
  }

  const rows = fields
    .map(([label, value]) =>
      `<tr><td style="padding:6px 12px 6px 0;font-weight:600;white-space:nowrap;vertical-align:top;color:#374151">${label}</td>` +
      `<td style="padding:6px 0;word-break:break-all;color:#111827">${value}</td></tr>`)
    .join('')

  const html = `<!DOCTYPE html>
<html><head>
<title>Certificate of Signing — v${sig.agreement_version}</title>
<style>
  @media print { body { margin: 0; } @page { margin: 20mm; } }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; font-size: 13px; color: #111827; padding: 40px; max-width: 700px; margin: 0 auto; }
  h1 { font-size: 22px; text-align: center; margin-bottom: 4px; }
  hr { border: none; border-top: 1px solid #d1d5db; margin: 16px 0; }
  table { width: 100%; border-collapse: collapse; }
  .footer { margin-top: 32px; font-size: 11px; color: #9ca3af; text-align: center; }
</style>
</head><body>
<h1>Certificate of Signing</h1>
<hr/>
<table>${rows}</table>
<hr/>
<p class="footer">Generated on ${new Date().toLocaleString('en-US')} — COM Platform</p>
</body></html>`

  const printWindow = window.open('', '_blank')
  if (!printWindow) return
  printWindow.document.write(html)
  printWindow.document.close()
  printWindow.addEventListener('afterprint', () => printWindow.close())
  // Small delay to let styles render before triggering print
  setTimeout(() => printWindow.print(), 250)
}

// =============================================================================
// Signature Certificate Card
// =============================================================================

function SignatureCertificateCard({ sig }: { sig: AgreementSignatureItem }) {
  const fields: { label: string; value: string }[] = [
    { label: 'Full Legal Name', value: sig.full_legal_name },
    { label: 'Email', value: sig.user_email },
    { label: 'IP Address', value: sig.ip_address },
    { label: 'User Agent', value: sig.user_agent },
    { label: 'Session ID', value: sig.session_id },
    { label: 'Agreement Version', value: `v${sig.agreement_version}` },
    { label: 'Signed At', value: formatDateTime(sig.signed_at) },
  ]

  if (sig.name_mismatch_confirmed && sig.name_mismatch_confirmed_at) {
    fields.push({ label: 'Name Mismatch', value: `Confirmed at ${formatDateTime(sig.name_mismatch_confirmed_at)}` })
  }

  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      <Text as="h3" variant="body" weight="bold" style={{ marginBottom: '16px' }}>
        Certificate of Signing
      </Text>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: '8px 16px',
      }}>
        {fields.map(({ label, value }) => (
          <div key={label} style={{ display: 'contents' }}>
            <Text variant="bodySm" color="muted">{label}</Text>
            <Text variant="bodySm" style={{ wordBreak: 'break-all' }}>{value}</Text>
          </div>
        ))}
      </div>
      <div style={{ marginTop: '16px' }}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadSignaturePdf(sig)}
        >
          Download PDF
        </Button>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

function InvestorAgreementPage() {
  const { id } = Route.useParams()
  const { data, isLoading } = useInvestorAgreement(id)

  if (isLoading) {
    return (
      <Stack gap="16" align="center" justify="center" style={{ minHeight: '400px' }}>
        <LoadingSpinner size="md" />
      </Stack>
    )
  }

  const signatures = data?.signatures ?? []
  const currentVersion = data?.current_version
  const latestSig = signatures[0] ?? null
  const isSigned = !!currentVersion

  return (
    <Stack gap="24">
      {/* Section 1: Current Status */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <Stack direction="row" gap="12" align="center" style={{ flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: 600,
            backgroundColor: isSigned ? '#DEF7EC' : '#F3F4F6',
            color: isSigned ? '#03543F' : '#6B7280',
          }}>
            {isSigned ? 'Signed' : 'Unsigned'}
          </span>
          {isSigned && latestSig && (
            <Text variant="bodySm" color="muted">
              Agreement Version: v{currentVersion} &mdash; Signed on {formatDateTime(latestSig.signed_at)}
            </Text>
          )}
        </Stack>
      </div>
      {/* Section 2: Signature Certificates */}
      {signatures.length > 0 ? (
        signatures.map((sig) => (
          <SignatureCertificateCard key={sig.id} sig={sig} />
        ))
      ) : (
        /* Section 3: Empty state */
        (<Stack gap="16" align="center" justify="center" style={{ padding: "80px 24px" }}>
          <Text variant="body" color="muted">No agreement signatures on record.</Text>
        </Stack>)
      )}
    </Stack>
  )
}
